import { useEffect, useRef, useState, type ReactNode } from 'react';
import { S, t, tList } from '../../i18n/strings';
import { usePrefersReducedMotion } from '../../lib/motion';
import { useSteve } from '../../state/context';
import { FIRST_DELAY_MS, nextDelay, nextTip, VISIBLE_MS } from './balloon';
import styles from './xp.module.css';

/**
 * La bulle de notification de la zone de notification.
 *
 * Elle sort 6 s après l'arrivée sur le skin, puis toutes les 45 à 70 s. Jamais
 * deux à la fois, jamais deux fois le même message de suite, et **rien ne tourne
 * quand l'onglet est masqué** : sinon on revient sur l'onglet et cinq bulles se
 * bousculent.
 *
 * Le choix du message et du délai est dans `balloon.ts`, sans DOM ; ici on ne
 * fait que poser les minuteurs et peindre.
 *
 * Elle sert aussi d'accusé de réception au menu Steve. Ses trois actions —
 * nourrir, complimenter, faire des bulles — vivent dans l'état partagé et se
 * voient dans le bac d'AquariumOS ; ici, il n'y a pas de bac, et elles ne
 * produisaient donc rien du tout. Le journal partagé sait ce qui vient de se
 * passer : la bulle le dit, ce qui est exactement le ton du système.
 */
interface Props {
  /**
   * Jeton du menu Steve : chaque incrément affiche `alert` sur-le-champ, en
   * coupant la file des astuces. Une valeur, pas un booléen — deux actions
   * d'affilée doivent produire deux bulles.
   */
  readonly alertToken?: number;
  /** La ligne de journal à annoncer, déjà localisée. */
  readonly alert?: string;
}

export function XpBalloon({ alertToken = 0, alert = '' }: Props): ReactNode {
  const { lang } = useSteve();
  const reducedMotion = usePrefersReducedMotion();
  const tips = tList(S.xp.balloon.tips, lang);

  const [shown, setShown] = useState<number | null>(null);
  const [forced, setForced] = useState<string | null>(null);
  // L'index vit dans une `ref` : le programmateur ne doit pas se relancer à
  // chaque bulle, sinon le rythme se réinitialise sans arrêt.
  const previous = useRef(-1);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const count = tips.length;
    if (count === 0) return;

    const clearTimers = (): void => {
      for (const id of timers.current) window.clearTimeout(id);
      timers.current = [];
    };

    const show = (): void => {
      const index = nextTip(count, previous.current);
      previous.current = index;
      setShown(index);
      timers.current.push(
        window.setTimeout(() => {
          setShown(null);
          schedule(nextDelay());
        }, VISIBLE_MS),
      );
    };

    const schedule = (delay: number): void => {
      timers.current.push(window.setTimeout(show, delay));
    };

    // Onglet masqué : on suspend tout et on repart d'un délai plein au retour.
    const onVisibility = (): void => {
      clearTimers();
      if (document.visibilityState === 'visible') schedule(FIRST_DELAY_MS);
      else setShown(null);
    };

    schedule(FIRST_DELAY_MS);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimers();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tips.length]);

  // Le menu Steve : on affiche sans attendre, et on s'efface tout seul.
  useEffect(() => {
    if (alertToken === 0 || alert === '') return;
    setForced(alert);
    const id = window.setTimeout(() => {
      setForced(null);
    }, VISIBLE_MS);
    return () => {
      window.clearTimeout(id);
    };
  }, [alertToken, alert]);

  // Une annonce du menu Steve passe devant l'astuce en cours.
  const text = forced ?? (shown === null ? undefined : tips[shown]);
  if (text === undefined || text === '') return null;

  const dismiss = (): void => {
    setForced(null);
    setShown(null);
  };

  return (
    <div
      className={styles.balloon}
      role="status"
      aria-live="polite"
      style={reducedMotion ? { animation: 'none' } : undefined}
      onClick={dismiss}
    >
      <span className={styles.balloonTitle}>{t(S.common.title, lang)}</span>
      <span className={styles.balloonText}>{text}</span>
      <button
        type="button"
        className={styles.balloonClose}
        aria-label={t(S.xp.balloon.close, lang)}
        onClick={(event) => {
          event.stopPropagation();
          dismiss();
        }}
      >
        ×
      </button>
      <span className={styles.balloonTail} aria-hidden="true" />
      <span className={styles.balloonTailInner} aria-hidden="true" />
    </div>
  );
}
